import {
  CUSTOM_SCHEDULE_MIN_PLAN,
  planAtLeast,
  START_OVER_ID,
  UNATTACHED_TRIGGERS,
  WAIT_MAX_MINUTES,
  WORKFLOW_STEP_MIN_PLAN,
  WORKFLOW_TEXT_STEP_TYPES,
  WORKFLOW_TRIGGER_MIN_PLAN,
  type PlanKey,
  type WaitUnit,
  type WorkflowTrigger,
} from "@sagot/constants";
import { waitMinutes } from "./builder/wait-model";
import type { Problem } from "./problem-model";
import type { Definition, Step, StepTranslation, WorkflowSettings } from "./types";

/** The rules of the backend's `graph_p4.py` (reusable workflows, webhook waits, translations) and `plan_rules.py` (what the plan includes). */

const problem = (code: string, where: string, extra: Partial<Pick<Problem, "path" | "stepId" | "params">> = {}): Problem => ({ code, where, params: {}, ...extra, source: "client" });

/** A reusable workflow has no settings of its own: it runs when another workflow calls it (backend `Settings()`). */
function hasDefaultSettings(settings: WorkflowSettings): boolean {
  return (
    settings.agent_ids.length === 0 &&
    settings.channels.length === 0 &&
    settings.schedule === "any" &&
    settings.custom_schedule === undefined &&
    settings.option_id === undefined &&
    settings.trigger_options === undefined &&
    settings.conditions.any.length === 0 &&
    settings.goal === undefined
  );
}

function translationProblems(definition: Definition): Problem[] {
  const steps = new Map<string, Step>();
  for (const path of Object.values(definition.paths)) for (const step of path.steps) steps.set(step.id, step);
  const problems: Problem[] = [];
  for (const [language, byStep] of Object.entries(definition.translations ?? {})) {
    for (const [stepId, texts] of Object.entries(byStep)) {
      const step = steps.get(stepId);
      const where = `translations.${language}.${stepId}`;
      if (!step) problems.push(problem("translation_unknown_step", where));
      else problems.push(...translatedWrongly(where, step, texts));
    }
  }
  return problems;
}

function translatedWrongly(where: string, step: Step, texts: StepTranslation): Problem[] {
  const problems: Problem[] = [];
  const what = (kind: string) => problem("translation_not_translatable", where, { stepId: step.id, params: { what: kind } });
  if (texts.text !== undefined && !WORKFLOW_TEXT_STEP_TYPES.has(step.type)) problems.push(what("text"));
  if (texts.field_label !== undefined && step.type !== "collect_data") problems.push(what("field_label"));
  if (texts.label !== undefined && step.type !== "show_article") problems.push(what("label"));
  if (texts.buttons === undefined) return problems;
  if (step.type !== "reply_buttons") return [...problems, what("buttons")];
  const known = new Set(step.buttons.map((button) => button.id));
  if (step.start_over) known.add(START_OVER_ID);
  for (const buttonId of Object.keys(texts.buttons)) if (!known.has(buttonId)) problems.push(problem("translation_unknown_button", where, { stepId: step.id, params: { button: buttonId } }));
  return problems;
}

export function p4Problems(definition: Definition, trigger: WorkflowTrigger): Problem[] {
  const problems: Problem[] = [];
  if (trigger === "reusable" && !hasDefaultSettings(definition.settings)) problems.push(problem("reusable_settings", "settings"));
  for (const [path, body] of Object.entries(definition.paths)) {
    for (const step of body.steps) {
      if (step.type === "wait_for_webhook" && UNATTACHED_TRIGGERS.has(trigger)) problems.push(problem("no_hook_here", `step ${step.id}`, { path, stepId: step.id }));
    }
  }
  return [...problems, ...translationProblems(definition)];
}

/** How long a step holds the run, when it does: a wait, a snooze for a while, the time a question waits for an answer, the time a booking or lead card stays open (backend `plan_rules._waited`). */
function heldFor(step: Step): { value: number; unit: WaitUnit } | undefined {
  switch (step.type) {
    case "wait":
      return step.duration;
    case "snooze":
      return step.for;
    case "collect_reply":
      return step.no_reply_after;
    case "show_booking":
      return step.not_booked_after;
    case "show_lead_form":
      return step.not_submitted_after;
    default:
      return undefined;
  }
}

/** What the plan does not include: the trigger, a custom schedule, a step, a wait that is too long. Checked when the workflow is published. */
export function planProblems(definition: Definition, trigger: WorkflowTrigger, plan: PlanKey): Problem[] {
  const problems: Problem[] = [];
  const needs = (where: string, minimum: PlanKey, place: Partial<Pick<Problem, "path" | "stepId">> = {}) =>
    problem("needs_plan", where, { ...place, params: { plan: minimum } });
  const triggerPlan = WORKFLOW_TRIGGER_MIN_PLAN[trigger];
  if (triggerPlan && !planAtLeast(plan, triggerPlan)) problems.push(needs("settings", triggerPlan));
  if (definition.settings.schedule === "custom" && !planAtLeast(plan, CUSTOM_SCHEDULE_MIN_PLAN)) problems.push(needs("settings", CUSTOM_SCHEDULE_MIN_PLAN));
  const cap = WAIT_MAX_MINUTES[plan];
  for (const [path, body] of Object.entries(definition.paths)) {
    for (const step of body.steps) {
      const where = `step ${step.id}`;
      const place = { path, stepId: step.id };
      const minimum = WORKFLOW_STEP_MIN_PLAN[step.type];
      if (minimum && !planAtLeast(plan, minimum)) problems.push(needs(where, minimum, place));
      const held = heldFor(step);
      if (held && waitMinutes(held) > cap) problems.push(problem("wait_too_long", where, { ...place, params: { minutes: cap } }));
    }
  }
  return problems;
}
